/**
 * History Initialization Hook
 * Connects the historyStore to templateStore and animationStore
 * Call this once at the app root to enable unified undo/redo
 */

import { useEffect, useRef } from 'react';
import { useHistoryStore } from '@/stores/historyStore';
import { useTemplateStore } from '@/stores/designStore';
import { useAnimationStore } from '@/stores/animationStore';
import type { DesignSnapshot } from '@/types/history';

export function useHistoryInit() {
  const initialized = useRef(false);

  const setSnapshotGetter = useHistoryStore((s) => s.setSnapshotGetter);
  const setSnapshotApplier = useHistoryStore((s) => s.setSnapshotApplier);
  const clearHistory = useHistoryStore((s) => s.clearHistory);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Set up snapshot getter - reads from both stores
    const getSnapshot = (): DesignSnapshot => {
      const templateState = useTemplateStore.getState();
      const animationState = useAnimationStore.getState();

      return {
        layers: templateState.currentTemplate?.layers ?? [],
        variables: templateState.currentTemplate?.variables ?? [],
        timeline: animationState.timeline,
      };
    };

    // Set up snapshot applier - writes to both stores
    const applySnapshot = (snapshot: DesignSnapshot) => {
      const templateStore = useTemplateStore.getState();
      const animationStore = useAnimationStore.getState();

      // Apply layers and variables to template
      if (templateStore.currentTemplate) {
        useTemplateStore.setState((state) => {
          if (state.currentTemplate) {
            state.currentTemplate.layers = snapshot.layers;
            state.currentTemplate.variables = snapshot.variables;
          }
          return state;
        });
      }

      // Apply timeline to animation store
      if (snapshot.timeline) {
        animationStore.setTimeline(snapshot.timeline);
      } else {
        animationStore.clearTimeline();
      }
    };

    setSnapshotGetter(getSnapshot);
    setSnapshotApplier(applySnapshot);

    // Clear history when template changes
    const unsubscribeTemplate = useTemplateStore.subscribe(
      (state, prevState) => {
        // If template ID changed (new template loaded), clear history
        if (state.currentTemplate?.metadata.id !== prevState.currentTemplate?.metadata.id) {
          clearHistory();
        }
      }
    );

    return () => {
      unsubscribeTemplate();
    };
  }, [setSnapshotGetter, setSnapshotApplier, clearHistory]);
}
